/**
 * Pure timestamp formatting helpers — no React, no context.
 *
 * Every UI surface that displays a time should route through here (or
 * the <Timestamp> component that wraps it). Calling `Date.toLocaleString`
 * directly is forbidden by lint — see eslint.config.mjs.
 *
 * All inputs are ISO 8601 strings (UTC, the shape every backend response
 * uses). All outputs are localized strings rendered in an explicit IANA
 * timezone — never the browser default by accident.
 */

export type TimestampVariant =
  /** Date + time, short ("25/04/2026, 01:22") */
  | "short"
  /** Date + time + seconds, long ("25 Apr 2026, 01:22:18") */
  | "long"
  /** Date only ("25 Apr 2026") */
  | "date"
  /** Year-less compact date, e.g. "Apr 25". For chart axes. */
  | "axis"
  /** Time only ("01:22") */
  | "time";

/**
 * Default locale used when the caller doesn't pass one. Derived from the
 * browser at module-load time. Stable for the lifetime of the tab.
 */
export const BROWSER_LOCALE: string =
  typeof navigator !== "undefined" && navigator.language
    ? navigator.language
    : "en-US";

/**
 * Default timezone derived from the browser. Used as the last-resort
 * fallback when no user / company TZ is available (e.g. login page).
 *
 * IMPORTANT: do not pass this to `formatChartTimestamp` — chart axes,
 * exports, and query-filter labels must derive their zone from the
 * data region. The `ChartTimezone` brand makes such misuse a compile
 * error; this constant remains for pre-auth pages and the resolver
 * fallback only.
 */
export const BROWSER_TZ: string =
  typeof Intl !== "undefined"
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : "UTC";

const VARIANT_OPTIONS: Record<TimestampVariant, Intl.DateTimeFormatOptions> = {
  short: {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  },
  long: {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  },
  date: {
    year: "numeric",
    month: "short",
    day: "2-digit",
  },
  /** Year-less compact date, e.g. "Apr 25". For chart axes. */
  axis: {
    month: "short",
    day: "numeric",
  },
  time: {
    hour: "2-digit",
    minute: "2-digit",
  },
};

/**
 * Format an ISO timestamp in a given IANA timezone.
 *
 * @param iso       ISO 8601 string (UTC), nullable. Returns "-" for null/empty.
 * @param timezone  IANA name (e.g. "America/New_York"). Required — no implicit
 *                  browser default. Pass `BROWSER_TZ` if you genuinely want it.
 * @param variant   Layout preset. Defaults to "short".
 * @param locale    BCP 47 locale tag. Defaults to the browser locale.
 */
export function formatTimestamp(
  iso: string | null | undefined,
  timezone: string,
  variant: TimestampVariant = "short",
  locale: string = BROWSER_LOCALE,
): string {
  if (!iso) return "-";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat(locale, {
    ...VARIANT_OPTIONS[variant],
    timeZone: timezone,
  }).format(date);
}

/**
 * Branded IANA-timezone string. The only legal source is
 * `useChartTimezone(regionId)` in [chart.ts](./chart.ts) — that hook
 * resolves the active mode (data / user / company) into a timezone and
 * tags it with this brand.
 *
 * `formatChartTimestamp` requires this brand, so passing `BROWSER_TZ`
 * (or any plain string) to a chart formatter is a *compile error*. This
 * is the structural guarantee that future widgets, query filters, and
 * Download Hub exports cannot bypass the data-region resolution.
 *
 * The brand is type-only; at runtime the value is just a string.
 */
declare const __chartTimezoneBrand: unique symbol;
export type ChartTimezone = string & {
  readonly [__chartTimezoneBrand]: never;
};

/**
 * Format an ISO timestamp for a chart axis, exported file, or query
 * filter label. The timezone argument MUST come from
 * `useChartTimezone(regionId)` — see `ChartTimezone` above.
 *
 * @param iso       ISO 8601 string (UTC), nullable. Returns "-" for null/empty.
 * @param timezone  Branded `ChartTimezone` from useChartTimezone().
 * @param variant   Layout preset. Defaults to "axis" (year-less compact date).
 * @param locale    BCP 47 locale tag. Defaults to the browser locale.
 */
export function formatChartTimestamp(
  iso: string | null | undefined,
  timezone: ChartTimezone,
  variant: TimestampVariant = "axis",
  locale: string = BROWSER_LOCALE,
): string {
  return formatTimestamp(iso, timezone as string, variant, locale);
}

/**
 * Short timezone abbreviation for a given IANA name at a given moment
 * (DST-aware — "EST" in winter, "EDT" in summer).
 */
export function tzAbbrev(
  timezone: string,
  when: Date | string = new Date(),
  locale: string = BROWSER_LOCALE,
): string {
  const date = typeof when === "string" ? new Date(when) : when;
  if (Number.isNaN(date.getTime())) return "";
  const parts = new Intl.DateTimeFormat(locale, {
    timeZone: timezone,
    timeZoneName: "short",
  }).formatToParts(date);
  return parts.find((p) => p.type === "timeZoneName")?.value ?? "";
}

/**
 * Relative time ("3h ago", "Just now"). Only the *delta* matters here, so
 * timezone is irrelevant — but locale controls the unit words.
 */
export function formatRelative(
  iso: string | null | undefined,
  locale: string = BROWSER_LOCALE,
): string {
  if (!iso) return "Never";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "Never";
  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.round(diffMs / 1000);
  const minutes = Math.floor(diffMs / 60_000);
  const hours = Math.floor(diffMs / 3_600_000);
  const days = Math.floor(diffMs / 86_400_000);
  if (Math.abs(diffSec) < 60) return "Just now";
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  if (Math.abs(minutes) < 60) return rtf.format(-minutes, "minute");
  if (Math.abs(hours) < 24) return rtf.format(-hours, "hour");
  if (Math.abs(days) < 7) return rtf.format(-days, "day");
  // Past a week, fall back to an absolute date — relative loses meaning.
  return formatTimestamp(iso, BROWSER_TZ, "date", locale);
}

/**
 * Default IANA timezone for global admins when they haven't set a
 * personal preference and aren't in a company context. The data-region
 * tooltip rows are surfaced separately from the registry.
 */
export const GLOBAL_ADMIN_DEFAULT_TZ = "Europe/Berlin";
